/**
 * Swipe settings (TRUS-761): which action each swipe direction runs, how far a
 * swipe must go, and whether a trackpad swipes at all.
 *
 * Stored per user and organization in the existing user-filter-preferences
 * store (`/api/user-filter-preferences/inbox-swipe`), so the same choices apply
 * in every browser, the desktop app and the mobile app, which reads the same
 * shape. Unknown keys written by a newer client are kept on save.
 *
 * Defaults are what TRUS-759/760 shipped: conversations right = pin, left =
 * read/unread; email right = read/unread, left = archive; messages right = reply.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { apiClient } from '@/services/apiClient';

export type ConversationSwipe = 'pin' | 'read' | 'none';
export type EmailSwipe = 'read' | 'archive' | 'none';
export type MessageSwipe = 'reply' | 'none';
export type SwipeSensitivity = 'light' | 'medium' | 'firm';

export interface SwipeSettings {
  version: 1;
  conversations: { right: ConversationSwipe; left: ConversationSwipe };
  email: { right: EmailSwipe; left: EmailSwipe };
  messages: { right: MessageSwipe };
  sensitivity: SwipeSensitivity;
  trackpad: boolean;
  /** Flip trackpad direction: Chrome cannot tell when macOS "natural scrolling" is off. */
  trackpadReverse: boolean;
}

export const SWIPE_SETTINGS_PATH = '/api/user-filter-preferences/inbox-swipe';

export const DEFAULT_SWIPE_SETTINGS: SwipeSettings = {
  version: 1,
  conversations: { right: 'pin', left: 'read' },
  email: { right: 'read', left: 'archive' },
  messages: { right: 'reply' },
  sensitivity: 'medium',
  trackpad: true,
  trackpadReverse: false,
};

/** Fraction of the row width a swipe must travel to act. */
export const SENSITIVITY_RATIO: Record<SwipeSensitivity, number> = { light: 0.2, medium: 0.35, firm: 0.5 };

const CONVERSATION: readonly ConversationSwipe[] = ['pin', 'read', 'none'];
const EMAIL: readonly EmailSwipe[] = ['read', 'archive', 'none'];
const MESSAGE: readonly MessageSwipe[] = ['reply', 'none'];
const SENSITIVITY: readonly SwipeSensitivity[] = ['light', 'medium', 'firm'];

function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function obj(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/** Any stored value -> valid settings; each bad or missing field falls back to its default. */
export function parseSwipeSettings(raw: unknown): SwipeSettings {
  const r = obj(raw);
  const c = obj(r.conversations);
  const e = obj(r.email);
  const m = obj(r.messages);
  const d = DEFAULT_SWIPE_SETTINGS;
  return {
    version: 1,
    conversations: {
      right: pick(c.right, CONVERSATION, d.conversations.right),
      left: pick(c.left, CONVERSATION, d.conversations.left),
    },
    email: { right: pick(e.right, EMAIL, d.email.right), left: pick(e.left, EMAIL, d.email.left) },
    messages: { right: pick(m.right, MESSAGE, d.messages.right) },
    sensitivity: pick(r.sensitivity, SENSITIVITY, d.sensitivity),
    trackpad: typeof r.trackpad === 'boolean' ? r.trackpad : d.trackpad,
    trackpadReverse: typeof r.trackpadReverse === 'boolean' ? r.trackpadReverse : d.trackpadReverse,
  };
}

/** The action configured for each side, from the actions this row can run. */
export function swipeSides<K extends string, A>(
  choice: { right: K | 'none'; left: K | 'none' },
  available: Partial<Record<K, A | undefined>>,
): { right?: A; left?: A } {
  return {
    right: choice.right === 'none' ? undefined : available[choice.right as K],
    left: choice.left === 'none' ? undefined : available[choice.left as K],
  };
}

// ---------------------------------------------------------------------------
// Store: one load per signed-in org, shared by every swipe row.
// ---------------------------------------------------------------------------

let current: SwipeSettings = DEFAULT_SWIPE_SETTINGS;
let stored: Record<string, unknown> = {};
let loadedFor: string | null = null;
const listeners = new Set<() => void>();

function emit(next: SwipeSettings) {
  current = next;
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function storeKey(): string | null {
  try {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    if (!token) return null;
    return `${localStorage.getItem('active_cbo_id') || ''}`;
  } catch {
    return null;
  }
}

/** Load the caller's settings once per org; failures keep the defaults. */
export function ensureSwipeSettingsLoaded(): void {
  const key = storeKey();
  if (key === null || key === loadedFor) return;
  loadedFor = key;
  apiClient
    .get(SWIPE_SETTINGS_PATH)
    .then((res) => {
      if (loadedFor !== key) return;
      stored = obj(res?.data?.filter_state);
      emit(parseSwipeSettings(stored));
    })
    .catch(() => {
      /* defaults stay; swiping still works */
    });
}

export function useSwipeSettings(): SwipeSettings {
  const settings = useSyncExternalStore(subscribe, () => current, () => current);
  useEffect(() => {
    ensureSwipeSettingsLoaded();
  }, []);
  return settings;
}

/** Save the whole settings object (optimistic; rolls back and rethrows on failure). */
export async function saveSwipeSettings(next: SwipeSettings): Promise<void> {
  const previous = current;
  const previousStored = stored;
  const body = { ...stored, ...next };
  emit(parseSwipeSettings(next));
  try {
    await apiClient.put(SWIPE_SETTINGS_PATH, { filter_state: body });
    stored = body;
  } catch (err) {
    stored = previousStored;
    emit(previous);
    throw err;
  }
}

/** Tests only. */
export function resetSwipeSettingsForTests(): void {
  current = DEFAULT_SWIPE_SETTINGS;
  stored = {};
  loadedFor = null;
  listeners.forEach((l) => l());
}
